import { useCallback, useEffect, useState } from "react";
import { Ban, CheckCircle2, Crown, LoaderCircle, Search, ShieldCheck, Store } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AdminUserSheet } from "@/pages/admin/AdminUserDetails";

const ROLE_CHIPS = [
  { value: "", label: "Tous" },
  { value: "user", label: "Usagers" },
  { value: "partner", label: "Partenaires" },
  { value: "moderator", label: "Modérateurs" },
  { value: "admin", label: "Admins" },
];

function UserRow({ u, self, canRoles, onChanged, onOpen }) {
  const [busy, setBusy] = useState(false);

  const patch = async (fields, okMsg) => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${u.id}`, fields);
      toast.success(okMsg);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de modifier ce compte.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`card-integra flex flex-wrap items-center gap-3 p-4 ${u.suspended ? "opacity-60" : ""}`} data-testid="admin-user-row">
      <button onClick={() => onOpen(u.id)} className="min-w-0 flex-1 text-left" data-testid="admin-user-open-details">
        <p className="flex flex-wrap items-center gap-1.5 text-sm font-bold text-slate-900 transition-colors hover:text-bleu">
          {u.name || "—"}
          {u.role === "super_admin" && <span className="flex items-center gap-1 rounded-full bg-bleu px-2 py-0.5 text-[9px] font-bold text-white"><ShieldCheck className="h-2.5 w-2.5" /> Super Admin</span>}
          {u.role === "admin" && <span className="flex items-center gap-1 rounded-full bg-bleu-light px-2 py-0.5 text-[9px] font-bold text-bleu"><ShieldCheck className="h-2.5 w-2.5" /> Admin</span>}
          {u.role === "moderator" && <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-bold text-amber-700"><ShieldCheck className="h-2.5 w-2.5" /> Modérateur</span>}
          {u.role === "partner" && <span className="flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[9px] font-bold text-slate-500"><Store className="h-2.5 w-2.5" /> Partenaire</span>}
          {u.plan === "premium" && <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-bold text-amber-700"><Crown className="h-2.5 w-2.5" /> Premium</span>}
          {u.suspended && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[9px] font-bold text-rose-600" data-testid="admin-user-suspended-badge">Suspendu</span>}
        </p>
        <p className="mt-0.5 text-xs text-slate-500">
          {u.email}
          {u.city && ` · ${u.city}`}
          {u.partner?.organization && ` · ${u.partner.organization}`}
          {u.created_at && ` · inscrit le ${new Date(u.created_at).toLocaleDateString("fr-FR")}`}
        </p>
      </button>
      {!self && u.role !== "super_admin" && (
        <div className="flex items-center gap-2">
          {canRoles && u.role !== "partner" && (
            <select value={u.role} disabled={busy}
              onChange={(e) => patch({ role: e.target.value }, "Rôle mis à jour.")}
              className="rounded-lg border border-slate-200 px-2 py-1.5 text-[11px] font-semibold text-slate-600 focus:outline-none"
              data-testid="admin-user-role-select">
              <option value="user">Utilisateur</option>
              <option value="moderator">Modérateur</option>
              <option value="admin">Administrateur</option>
            </select>
          )}
          <button disabled={busy}
            onClick={() => patch({ suspended: !u.suspended }, u.suspended ? "Compte réactivé." : "Compte suspendu.")}
            className={`flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[11px] font-bold transition-colors ${
              u.suspended
                ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                : "border-slate-200 text-slate-500 hover:border-rouge/40 hover:text-rouge"
            }`}
            data-testid="admin-user-suspend-button">
            {busy ? <LoaderCircle className="h-3 w-3 animate-spin" /> : u.suspended ? <CheckCircle2 className="h-3 w-3" /> : <Ban className="h-3 w-3" />}
            {u.suspended ? "Réactiver" : "Suspendre"}
          </button>
        </div>
      )}
      {self && <span className="text-[10px] font-bold uppercase tracking-wider text-slate-300">Vous</span>}
    </div>
  );
}

export default function AdminUsers() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/users", { params: { q: q || undefined, role: role || undefined, page } });
      setData(d);
    } catch {
      toast.error("Impossible de charger les utilisateurs.");
    } finally {
      setLoading(false);
    }
  }, [q, role, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="mx-auto max-w-4xl space-y-5" data-testid="page-admin-users">
      <div>
        <h1 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">Utilisateurs</h1>
        <p className="mt-1 text-sm text-slate-500">Recherchez, gérez les rôles et suspendez des comptes ({data?.total ?? "…"} au total).</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-300" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Rechercher par nom ou email…"
            className="w-full rounded-full border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm placeholder:text-slate-400 focus:border-bleu focus:outline-none"
            data-testid="admin-users-search-input" />
        </div>
        {ROLE_CHIPS.map((c) => (
          <button key={c.value} onClick={() => { setRole(c.value); setPage(1); }}
            className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${
              role === c.value ? "bg-bleu text-white" : "border border-slate-200 bg-white text-slate-500 hover:border-bleu/40 hover:text-bleu"
            }`}
            data-testid={`admin-users-filter-${c.value || "all"}`}>
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><LoaderCircle className="h-6 w-6 animate-spin text-slate-300" /></div>
      ) : !data || data.users.length === 0 ? (
        <div className="card-integra p-10 text-center" data-testid="admin-users-empty">
          <p className="text-sm font-bold text-slate-700">Aucun utilisateur trouvé</p>
        </div>
      ) : (
        <>
          <div className="space-y-2.5">
            {data.users.map((u) => <UserRow key={u.id} u={u} self={u.id === user?.id} canRoles={user?.role === "super_admin"} onChanged={load} onOpen={setOpenId} />)}
          </div>
          {data.pages > 1 && (
            <div className="flex items-center justify-center gap-3" data-testid="admin-users-pagination">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                className="rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-slate-500 disabled:opacity-40">Précédent</button>
              <span className="text-xs font-bold text-slate-400">Page {data.page} / {data.pages}</span>
              <button disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}
                className="rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-slate-500 disabled:opacity-40">Suivant</button>
            </div>
          )}
        </>
      )}

      <AdminUserSheet userId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
